import React, { useState, useEffect } from 'react'
import {
  Card, Form, Input, Button, Row, Col, Select, InputNumber, message,
  Divider, Checkbox, Tabs, Tooltip, Alert, Table, DatePicker, Tag,
  Switch, Upload, Modal, Radio,
} from 'antd'
import {
  SaveOutlined, EyeOutlined, ArrowLeftOutlined, CopyOutlined,
  DeleteOutlined, PlusOutlined, FileTextOutlined, FileProtectOutlined,
  PictureOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { SupplySource, PricingDetail, Lead, LeadStage, type ProposalFieldChange } from '@/types'
import { leadStore } from '../Lead/leadStore'
import { proposalStore, type ProposalRow } from './proposalStore'
import { ProposalStatus } from '@/types'
import { useMasterData, type ProposalTemplate } from '@/context/MasterDataContext'
import RichTextEditor from '@/components/RichTextEditor'
import { buildTemplateBody, getTemplateBody, getTemplateTermsHtml } from '@/utils/proposalTemplateBody'
import { authStore } from '@/context/auth'
import '../Lead/LeadList.css'
import dayjs from 'dayjs'
import { generateCode } from '@/utils/codeGenerator'
import { genId } from '@/utils/genId'
import { ApiError } from '@/services/api'

// ── Negotiation change tracking ─────────────────────────────────────────────
// Fields worth surfacing in Revision History when an already-Approved
// proposal is edited via "Negotiate". Deliberately limited to
// commercial/contract terms — rich-text fields (body/terms/company content)
// aren't diffed here since an HTML diff wouldn't be readable in a timeline.
const PROPOSAL_CHANGE_FIELDS: { path: string[]; label: string }[] = [
  { path: ['tariff'], label: 'TNEB Tariff (₹/kWh)' },
  { path: ['powerFacilitationCharge'], label: 'Sakthi PF Charge (₹/unit)' },
  { path: ['gstOnFacilitationCharge'], label: 'GST on Facilitation Charge (%)' },
  { path: ['commercialTerms'], label: 'Payment Terms' },
  { path: ['validity'], label: 'Validity (days)' },
  { path: ['monthlyConsumption'], label: 'Monthly Consumption (kWh)' },
  { path: ['energySupplyAdjustments'], label: 'Energy Supply & Adjustments' },
  { path: ['additionalTerms'], label: 'Additional Terms' },
  { path: ['contractTerms', 'contractPeriod'], label: 'Contract Period (yrs)' },
  { path: ['contractTerms', 'lockInPeriod'], label: 'Lock-in Period (yrs)' },
  { path: ['contractTerms', 'noticePeriod'], label: 'Notice Period (months)' },
  { path: ['contractTerms', 'commencementMonth'], label: 'Commencement Month' },
  { path: ['billingTerms', 'billRaiseMethod'], label: 'Bill Raising Method' },
  { path: ['billingTerms', 'paymentDueInDays'], label: 'Payment Due (days)' },
  { path: ['billingTerms', 'minimumAssuredSupply'], label: 'Minimum Assured Supply' },
  { path: ['billingTerms', 'rateRevision'], label: 'Rate Revision' },
]

const getAtPath = (obj: any, path: string[]) => path.reduce((o, k) => (o == null ? o : o[k]), obj)
const displayVal = (v: any) => (v === undefined || v === null || v === '' ? '—' : String(v))
const summarizePricing = (rows?: PricingDetail[]) =>
  (rows || []).map(r => `${r.supplySource || '—'}: ₹${Number(r.rate || 0).toFixed(2)}`).join(', ') || '—'

/** Diff two ProposalRow-like records over the fields above; pricingDetails gets one summarized entry. */
function buildProposalChanges(oldRec: Partial<ProposalRow>, newRec: Partial<ProposalRow>): ProposalFieldChange[] {
  const changes: ProposalFieldChange[] = []
  for (const f of PROPOSAL_CHANGE_FIELDS) {
    const oldV = getAtPath(oldRec, f.path)
    const newV = getAtPath(newRec, f.path)
    if (String(oldV ?? '') !== String(newV ?? '')) {
      changes.push({ field: f.path.join('.'), label: f.label, oldValue: displayVal(oldV), newValue: displayVal(newV) })
    }
  }
  const oldPricing = summarizePricing(oldRec.pricingDetails)
  const newPricing = summarizePricing(newRec.pricingDetails)
  if (oldPricing !== newPricing) {
    changes.push({ field: 'pricingDetails', label: 'Pricing Details', oldValue: oldPricing, newValue: newPricing })
  }
  return changes
}

// Commercial Terms fields exposed as {FIELD NAME} placeholders in the
// Proposal Body / Terms & Conditions — mirrors the tokens proposalPdfGenerator
// resolves at download time (see COMMERCIAL_FIELD_PLACEHOLDERS in ProposalTemplatesMaster.tsx).
const COMMERCIAL_TERMS_PLACEHOLDERS = [
  'CONTRACT PERIOD', 'LOCK IN PERIOD', 'NOTICE PERIOD', 'COMMENCEMENT MONTH',
  'VALIDITY', 'COMMERCIAL TERMS', 'POWER FACILITATION CHARGE', 'GST ON FACILITATION CHARGE',
  'PAYMENT DUE IN DAYS', 'MINIMUM ASSURED SUPPLY', 'RATE REVISION', 'BILL RAISE METHOD',
]

const SECURITY_MECHANISM_OPTIONS = [
  { label: 'Bank Guarantee — 45 days', value: 'Bank Guarantee (45 days)' },
  { label: 'Bank Guarantee — 60 days', value: 'Bank Guarantee (60 days)' },
  { label: 'Bank Guarantee — 90 days', value: 'Bank Guarantee (90 days)' },
  { label: 'Letter of Credit',         value: 'Letter of Credit' },
  { label: 'Undated PDC',              value: 'Undated PDC' },
  { label: 'Cash Deposit',             value: 'Cash Deposit' },
]

const CoverImageUpload: React.FC<{ value?: string; onChange?: (v: string) => void }> = ({ value, onChange }) => {
  const toBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <Upload
        accept="image/*"
        showUploadList={false}
        beforeUpload={async (file) => {
          if (file.size > 2 * 1024 * 1024) {
            message.error('Image must be under 2 MB')
            return Upload.LIST_IGNORE
          }
          try {
            onChange?.(await toBase64(file))
          } catch {
            message.error('Could not read the image')
          }
          return false
        }}
      >
        <Button icon={<PictureOutlined />}>{value ? 'Change Image' : 'Upload Image'}</Button>
      </Upload>
      {value && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={value} alt="cover" style={{ height: 48, borderRadius: 4, border: '1px solid #e5e7eb' }} />
          <Button size="small" danger type="text" icon={<DeleteOutlined />} onClick={() => onChange?.('')}>
            Remove
          </Button>
        </div>
      )}
    </div>
  )
}

const ProposalForm: React.FC = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const isEditing = !!id

  const fromLead: Lead | undefined = location.state?.fromLead

  const allLeads = leadStore.getLeads()
  const [selectedLeadId, setSelectedLeadId] = useState<string>(fromLead?.id ?? '')

  // ── Templates + Tariffs from Master (T&C come from the selected template) ──
  const { proposalTemplates, tariffs, tnebTariffs, consumers, generators, powerTypes, drawalVoltages, saveTariff } = useMasterData()
  const activeTemplates = proposalTemplates.filter(t => t.status === 'active')
  const drawlVoltageOptions = drawalVoltages
    .filter(v => v.status === 'active')
    .map(v => ({ label: `${v.value} ${v.unit}`, value: `${v.value} ${v.unit}` }))
  const watchedRequiredDocs = (Form.useWatch('requiredDocuments', form) as string[] | undefined) ?? []
  const watchedSigningDocs  = (Form.useWatch('signingDocuments', form) as string[] | undefined) ?? []

  // Watch live form values for the green banner so it reflects real-time edits
  const watchedContractTerms        = Form.useWatch('contractTerms', form) as any
  const watchedBillingTerms         = Form.useWatch('billingTerms', form) as any
  const watchedPfCharge             = Form.useWatch('sakthiTariff', form) as number | undefined
  const watchedGst                  = Form.useWatch('gstOnFacilitationCharge', form) as number | undefined
  const watchedValidity             = Form.useWatch('validity', form) as number | undefined
  const watchedCoverPageEnabled     = Form.useWatch('coverPageEnabled', form) as boolean | undefined
  const watchedCompanyContentHtml   = Form.useWatch('companyContentHtml', form) as string | undefined
  
  // Quick Add Tariff Modal state
  const [addTariffModalOpen, setAddTariffModalOpen] = useState(false)
  const [addTariffForm] = Form.useForm()
  const [addTariffSubmitting, setAddTariffSubmitting] = useState(false)

  // Loading state for the main proposal form's submit button — distinct from
  // addTariffSubmitting above, which belongs to the unrelated Quick Add
  // Tariff sub-modal.
  const [submitting, setSubmitting] = useState(false)

  const activeSakthiTariffs = tariffs.filter(t => t.status === 'active')
  const getSakthiPfCharge = (t: typeof activeSakthiTariffs[0]) =>
    t.powerFacilitationCharge ?? t.ratePerUnit ?? 0

  const selectedLeadObj: Lead | undefined = allLeads.find(l => l.id === selectedLeadId)

  const matchedConsumer = selectedLeadObj
    ? consumers.find(c =>
        (c.htscNumber && selectedLeadObj.htscNumber && c.htscNumber.trim() === selectedLeadObj.htscNumber.trim()) ||
        c.name.toLowerCase().trim() === selectedLeadObj.companyName.toLowerCase().trim() ||
        c.name.toLowerCase().includes(selectedLeadObj.companyName.toLowerCase()) ||
        selectedLeadObj.companyName.toLowerCase().includes(c.name.toLowerCase()) ||
        (c as any).leadId === selectedLeadId
      )
    : undefined

  const matchedGenerator = selectedLeadObj
    ? generators.find(g =>
        g.name.toLowerCase().trim() === selectedLeadObj.companyName.toLowerCase().trim() ||
        g.name.toLowerCase().includes(selectedLeadObj.companyName.toLowerCase()) ||
        selectedLeadObj.companyName.toLowerCase().includes(g.name.toLowerCase()) ||
        (g as any).leadId === selectedLeadId
      )
    : undefined

  const relevantSakthiTariffs = (() => {
    if (matchedConsumer) {
      const specific = activeSakthiTariffs.filter(t => t.consumerId === matchedConsumer.id)
      const general = activeSakthiTariffs.filter(t => !t.consumerId && !t.generatorId)
      return specific.length > 0 ? specific : general
    }
    if (matchedGenerator) {
      const specific = activeSakthiTariffs.filter(t => t.generatorId === matchedGenerator.id)
      const general = activeSakthiTariffs.filter(t => !t.consumerId && !t.generatorId)
      return specific.length > 0 ? specific : general
    }
    if (selectedLeadObj) {
      const leadSpecific = activeSakthiTariffs.filter(t =>
        (t as any).leadId === selectedLeadId ||
        t.description?.toLowerCase().includes(selectedLeadObj.companyName.toLowerCase())
      )
      const general = activeSakthiTariffs.filter(t => !t.consumerId && !t.generatorId)
      return leadSpecific.length > 0 ? leadSpecific : general
    }
    const general = activeSakthiTariffs.filter(t => !t.consumerId && !t.generatorId)
    return general.length > 0 ? general : activeSakthiTariffs
  })()

  const hasExistingSakthiTariff = (() => {
    if (matchedConsumer) {
      return activeSakthiTariffs.some(t => t.consumerId === matchedConsumer.id)
    }
    if (matchedGenerator) {
      return activeSakthiTariffs.some(t => t.generatorId === matchedGenerator.id)
    }
    if (selectedLeadObj) {
      return activeSakthiTariffs.some(t =>
        (t as any).leadId === selectedLeadId ||
        t.description?.toLowerCase().includes(selectedLeadObj.companyName.toLowerCase())
      )
    }
    return false
  })()

  const sakthiTariffOptions = relevantSakthiTariffs.map(t => {
    const charge = getSakthiPfCharge(t)
    return {
      label: `${t.tariffCode} — ${(charge * 100).toFixed(0)} Paise/unit`,
      value: charge,
    }
  })

  const activeTnebTariffs = tnebTariffs.filter(t => t.status === 'active')
  const tnebTariffOptions = activeTnebTariffs.map(t => ({
    label: `${t.code} — ₹${t.ratePerUnit}/kWh (${t.description})`,
    value: t.ratePerUnit,
  }))
  const [selectedTemplate, setSelectedTemplate] = useState<ProposalTemplate | null>(null)
  // Status the proposal had when this edit session started — used to decide
  // whether this is a "Negotiate" edit (started life Approved) that needs its
  // changes recorded in Revision History.
  const [originalStatus, setOriginalStatus] = useState<ProposalStatus | undefined>(undefined)
  const isNegotiating = isEditing && originalStatus === ProposalStatus.APPROVED

  // ── One active proposal per lead ─────────────────────────────────────
  // A lead can only have one proposal "in flight" at a time — once that
  // proposal is Rejected or Expired, a fresh one can be created for the same
  // lead again. Updating an existing proposal (isEditing) is unaffected;
  // this only guards against creating a brand-new one.
  const existingActiveProposalForLead = (!isEditing && selectedLeadId)
    ? proposalStore.getAll().find(p =>
        p.leadId === selectedLeadId &&
        p.status !== ProposalStatus.REJECTED &&
        p.status !== ProposalStatus.EXPIRED &&
        p.status !== ProposalStatus.LOST
      )
    : undefined

  // ── Lead info ─────────────────────────────────────────────────────────
  const leadInfo = selectedLeadObj ? {
    name: selectedLeadObj.companyName,
    contactPerson: selectedLeadObj.customerName,
    email: selectedLeadObj.email,
    phone: selectedLeadObj.contactNumber,
    city: selectedLeadObj.city,
    address: selectedLeadObj.address,
    sanctionedDemand: selectedLeadObj.sanctionedDemand,
    drawlVoltage: selectedLeadObj.drawlVoltage,
  } : null

  // Missing HTSC/GST no longer blocks proposal creation — a lead can be
  // converted to a proposal at any time, deactive or not. This label is
  // purely informational so staff know to follow up on HTSC/GST separately.
  const isLeadDeactive = (l: Lead) => l.isDeactive || !l.htscNumber?.trim() || !l.gstNumber?.trim()

  // Same "one active proposal per lead" rule existingActiveProposalForLead
  // enforces after the fact (see below) — applied here up front so a lead
  // that's already been converted to a proposal never shows up as pickable
  // in the first place. A lead whose only proposal was Rejected/Expired/Lost
  // is still open for a fresh one, matching that same rule. The proposal's
  // own lead stays visible while editing it, so the field doesn't go blank.
  const leadHasActiveProposal = (leadId: string): boolean =>
    proposalStore.getAll().some(p =>
      p.leadId === leadId &&
      p.status !== ProposalStatus.REJECTED &&
      p.status !== ProposalStatus.EXPIRED &&
      p.status !== ProposalStatus.LOST
    )

  const leadOptions = allLeads
    .filter(l => (isEditing && l.id === selectedLeadId) || !leadHasActiveProposal(l.id))
    .map(l => {
      const deactive = isLeadDeactive(l)
      return {
        label: deactive ? `${l.companyName} [Missing HTSC/GST]` : l.companyName,
        value: l.id,
      }
    })

  // Auto-generated reference number for new proposals — plain SP/<FY>/<seq>
  // format, not tied to Code Master (Proposal ID is what uses Code Master).
  const fyLabel = (() => {
    const d = new Date()
    const y = d.getFullYear()
    const s = d.getMonth() >= 3 ? y : y - 1   // FY starts April
    return `${s}-${String((s + 1) % 100).padStart(2, '0')}`
  })()
  const autoRefNumber = `SP/${fyLabel}/${String(proposalStore.getAll().length + 1).padStart(4, '0')}`

  const handleLeadChange = (val: string) => {
    setSelectedLeadId(val)
    const lead = allLeads.find(l => l.id === val)
    if (lead) {
      form.setFieldsValue({ gstDetails: { placeOfSupply: lead.city } })

      // Try to find matching consumer or generator from master
      const matchedConsumer = consumers.find(c => c.htscNumber === lead.htscNumber || c.name.toLowerCase() === lead.companyName.toLowerCase())
      const matchedGenerator = generators.find(g => g.name.toLowerCase() === lead.companyName.toLowerCase())

      // Find specific or general fallback Sakthi tariff for the matched consumer or generator or lead
      const matchedSakthi = tariffs.find(t =>
        t.status === 'active' &&
        ((matchedConsumer && t.consumerId === matchedConsumer.id) ||
         (matchedGenerator && t.generatorId === matchedGenerator.id) ||
         (t as any).leadId === val ||
         (t.description && t.description.toLowerCase().includes(lead.companyName.toLowerCase())))
      ) || tariffs.find(t =>
        t.status === 'active' &&
        matchedConsumer &&
        t.tariffCode === matchedConsumer.tariffCode
      ) || tariffs.find(t =>
        t.status === 'active' && !t.consumerId && !t.generatorId
      ) || tariffs.find(t => t.status === 'active')

      if (matchedSakthi) {
        form.setFieldValue('sakthiTariff', getSakthiPfCharge(matchedSakthi))
      }

      // Find TNEB tariff matching the consumer category
      if (matchedConsumer) {
        const matchedTneb = tnebTariffs.find(t =>
          t.status === 'active' &&
          (t.code === matchedConsumer.powerTariffCategory || t.category === matchedConsumer.powerTariffCategory)
        )
        if (matchedTneb) {
          form.setFieldValue('tariff', matchedTneb.ratePerUnit)
        }
      }

      // Auto-fill volume/annum in pricing details from lead's Required Units Per Annum
      if (lead.requiredUnitsPerAnnum) {
        const reqVol = `${Number(lead.requiredUnitsPerAnnum).toLocaleString('en-IN')} units`
        setPricingRows(prev => prev.map((row, idx) => idx === 0 ? { ...row, volumePerAnnum: reqVol } : row))
      }
    }
  }

  // ── Template selection → auto-fill form from master data ─────────────
  const handleTemplateChange = (templateId: string) => {
    const tpl = activeTemplates.find(t => t.id === templateId)
    if (!tpl) return
    setSelectedTemplate(tpl)

    // Pre-fill pricing row with template defaults
    setPricingRows([{
      supplySource: SupplySource.WIND_SOLAR,
      volumePerAnnum: '',
      drawlVoltage: leadInfo?.drawlVoltage || '',
      rate: 0,
      equityInvestment: 'NIL',
      securityMechanism: '',
    }])

    // Auto-select first active tariff from master
    const defaultSakthi = activeSakthiTariffs.find(t =>
      t.applicableTo === 'both' || t.applicableTo === 'consumer'
    ) ?? activeSakthiTariffs[0]

    const defaultTneb = activeTnebTariffs[0]

    // Pre-fill all form fields
    form.setFieldsValue({
      contractTerms: {
        contractPeriod: tpl.contractPeriod,
        lockInPeriod: tpl.lockInPeriod,
        noticePeriod: tpl.noticePeriod,
        commencementMonth: tpl.commencementMonth,
      },
      billingTerms: {
        billRaiseMethod: tpl.billRaiseMethod,
        paymentDueInDays: tpl.paymentDueInDays,
        minimumAssuredSupply: tpl.minimumAssuredSupply,
        rateRevision: tpl.rateRevision,
      },
      gstOnFacilitationCharge: tpl.gstOnFacilitationCharge,
      tariff: defaultTneb?.ratePerUnit,
      sakthiTariff: defaultSakthi ? getSakthiPfCharge(defaultSakthi) : undefined,
      commercialTerms: tpl.commercialTerms,
      validity: tpl.validity,
      energySupplyAdjustments: tpl.energyAdjustmentClauses.join('\n'),
      requiredDocuments: tpl.requiredDocuments,    // pre-fill required docs from template
      signingDocuments: tpl.signingDocuments,      // pre-fill docs-to-sign from template
      gstDetails: {
        hsn: '271311',
        placeOfSupply: leadInfo?.city || fromLead?.city || 'Tamil Nadu',
      },
      // pre-fill custom document content overrides from template
      bodyHtml: tpl.bodyHtml && tpl.bodyHtml.trim() ? tpl.bodyHtml : buildTemplateBody(tpl),
      companyContentHtml: tpl.companyContentHtml,
      coverPageEnabled: tpl.coverPageEnabled,
      coverTitle: tpl.coverTitle,
      coverSubtitle: tpl.coverSubtitle,
      coverDescription: tpl.coverDescription,
      coverImage: tpl.coverImage,
      termsConditionsHtml: getTemplateTermsHtml(tpl),
    })

    message.success(`"${tpl.name}" template applied — fields pre-filled!`)
  }

  const [previewMode, setPreviewMode] = useState(false)
  const [formValues, setFormValues] = useState<any>(null)
  const [pricingRows, setPricingRows] = useState<PricingDetail[]>([{
    supplySource: SupplySource.WIND_SOLAR,
    volumePerAnnum: '',
    drawlVoltage: '',
    rate: 0,
    equityInvestment: 'NIL',
    securityMechanism: '',
  }])

  const supplySourceOptions = powerTypes.filter(p => p.status === 'active').map(p => ({
    label: p.name, value: p.name,
  }))

  // ── Edit mode: load the persisted proposal and prefill the form ─────────
  useEffect(() => {
    if (!id) return
    const existing = proposalStore.get(id)
    if (!existing) return
    // Won is a closed deal — nobody negotiates it further (backend rejects
    // the save either way; this just stops someone from filling out the
    // whole form before finding that out via a direct/bookmarked edit link).
    if (existing.status === ProposalStatus.WON) {
      message.error('This proposal is Won and closed — it can no longer be edited or negotiated.')
      navigate(`/proposals/${id}`)
      return
    }
    setOriginalStatus(existing.status)
    setSelectedLeadId(existing.leadId)
    if (existing.pricingDetails?.length) setPricingRows(existing.pricingDetails)
    const tpl = existing.templateId
      ? proposalTemplates.find(t => t.id === existing.templateId)
      : proposalTemplates.find(t => t.name === existing.template)
    if (tpl) setSelectedTemplate(tpl)
    form.setFieldsValue({
      leadId: existing.leadId,
      template: tpl?.id,
      referenceNumber: existing.referenceNumber,
      offerDate: existing.offerDate ? dayjs(existing.offerDate) : undefined,
      tariff: existing.tariff,
      commercialTerms: existing.commercialTerms,
      validity: existing.validity,
      sakthiTariff: existing.powerFacilitationCharge,
      gstOnFacilitationCharge: existing.gstOnFacilitationCharge,
      contractTerms: existing.contractTerms,
      billingTerms: existing.billingTerms,
      energySupplyAdjustments: existing.energySupplyAdjustments,
      gstDetails: existing.gstDetails,
      additionalTerms: existing.additionalTerms,
      monthlyConsumption: existing.monthlyConsumption,
      requiredDocuments: existing.requiredDocuments,
      signingDocuments: existing.signingDocuments,
      // load overrides if present, otherwise fall back to template defaults
      bodyHtml: existing.bodyHtml !== undefined ? existing.bodyHtml : (tpl ? (tpl.bodyHtml && tpl.bodyHtml.trim() ? tpl.bodyHtml : buildTemplateBody(tpl)) : ''),
      companyContentHtml: existing.companyContentHtml !== undefined ? existing.companyContentHtml : tpl?.companyContentHtml,
      coverPageEnabled: existing.coverPageEnabled !== undefined ? existing.coverPageEnabled : tpl?.coverPageEnabled,
      coverTitle: existing.coverTitle !== undefined ? existing.coverTitle : tpl?.coverTitle,
      coverSubtitle: existing.coverSubtitle !== undefined ? existing.coverSubtitle : tpl?.coverSubtitle,
      coverDescription: existing.coverDescription !== undefined ? existing.coverDescription : tpl?.coverDescription,
      coverImage: existing.coverImage !== undefined ? existing.coverImage : tpl?.coverImage,
      termsConditionsHtml: existing.termsConditionsHtml !== undefined && existing.termsConditionsHtml !== null ? existing.termsConditionsHtml : (tpl ? getTemplateTermsHtml(tpl) : ''),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, proposalTemplates])

  const onFinish = async (values: any) => {
    setSubmitting(true)
    try {
      // ── Persist a slim, list-facing ProposalRow ───────────────────────
      const leadId = values.leadId || selectedLeadId || fromLead?.id || ''
      const lead = allLeads.find(l => l.id === leadId)
      const leadName = lead?.companyName || fromLead?.companyName || ''

      // Re-check the one-active-proposal-per-lead rule right before saving —
      // the Step 1 alert/disabled-button already guard this in the UI, but
      // this is the actual enforcement point in case that state is stale.
      if (!isEditing) {
        const activeForLead = proposalStore.getAll().find(p =>
          (p.leadId === leadId || (p.leadName && leadName && p.leadName.trim().toLowerCase() === leadName.trim().toLowerCase())) &&
          p.status !== ProposalStatus.REJECTED && p.status !== ProposalStatus.EXPIRED && p.status !== ProposalStatus.LOST
        )
        if (activeForLead) {
          if (leadId || lead?.id) {
            await leadStore.saveLeadAndConfirm({ id: leadId || lead?.id, stage: LeadStage.WON, convertedToProposal: true })
          }
          message.info(`${leadName || leadId} already has an active proposal (${activeForLead.referenceNumber || activeForLead.id}). Opening proposal...`)
          setTimeout(() => navigate(`/proposals/${activeForLead.id}`), 1000)
          return
        }
      }

      const completeValues = { ...values, pricingDetails: pricingRows }
      setFormValues(completeValues)

      const existing = isEditing && id ? proposalStore.get(id) : undefined
      // Plain SP/<FY>/<seq> format — not tied to Code Master. Reuse the same
      // value already computed for the read-only preview field instead of
      // recomputing it here (avoids the two ever drifting out of sync).
      const referenceNumber = existing?.referenceNumber || autoRefNumber

      // Proposal ID comes from Code Master (module "proposal_ids") — the
      // Reference Number above is a plain SP/<FY>/<seq> string, not Code
      // Master-driven, so the two don't need to match. Falls back to the
      // store's own random id generator if the call fails.
      let generatedId: string | undefined
      if (!isEditing) {
        try {
          generatedId = await generateCode('proposal_ids')
        } catch { /* fall back to proposalStore's own id generator */ }
      }

      const validity = Number(values.validity) || 30
      const today = new Date()
      const expiry = new Date(today)
      expiry.setDate(today.getDate() + validity)
      const iso = (d: Date) => d.toISOString().slice(0, 10)

      const record: Omit<ProposalRow, 'id'> = {
        referenceNumber,
        leadId,
        leadName,
        revisionNumber: existing?.revisionNumber ?? 1,
        tariff: Number(values.tariff) || 0,
        commercialTerms: values.commercialTerms || '',
        status: existing?.status ?? (isEditing ? ProposalStatus.SUBMITTED : ProposalStatus.DRAFT),
        validity,
        createdAt: existing?.createdAt ?? iso(today),
        expiryDate: iso(expiry),
        template: selectedTemplate?.name,
        templateId: selectedTemplate?.id,
        requiredDocuments: values.requiredDocuments || selectedTemplate?.requiredDocuments || [],
        signingDocuments: values.signingDocuments || selectedTemplate?.signingDocuments || [],
        // ── Full payload so the Detail view / PDF reflect what was entered ──
        offerDate: values.offerDate ? dayjs(values.offerDate).format('YYYY-MM-DD') : iso(today),
        pricingDetails: pricingRows,
        powerFacilitationCharge: values.sakthiTariff,
        gstOnFacilitationCharge: values.gstOnFacilitationCharge,
        contractTerms: values.contractTerms,
        billingTerms: values.billingTerms,
        energySupplyAdjustments: values.energySupplyAdjustments,
        gstDetails: values.gstDetails,
        additionalTerms: values.additionalTerms,
        monthlyConsumption: values.monthlyConsumption,
        // Custom text / layout overrides
        bodyHtml: values.bodyHtml,
        companyContentHtml: values.companyContentHtml,
        coverPageEnabled: values.coverPageEnabled,
        coverTitle: values.coverTitle,
        coverSubtitle: values.coverSubtitle,
        coverDescription: values.coverDescription,
        coverImage: values.coverImage,
        termsConditionsHtml: values.termsConditionsHtml,
      }

      // Negotiation history: only recorded when this proposal was Approved at
      // the moment editing started — routine Draft/Submitted edits aren't
      // "negotiations" and would just add noise to Revision History.
      if (isNegotiating && existing) {
        const changes = buildProposalChanges(existing, record)
        if (changes.length) {
          const signedIn = authStore.getUser()
          record.changeLog = [
            ...(existing.changeLog ?? []),
            { date: new Date().toISOString(), changedBy: signedIn?.name || signedIn?.email || 'Unknown', changes },
          ]
        }
      }

      const saved = proposalStore.save({ id: isEditing ? id : generatedId, ...record })
      const savedId = (saved as { id?: string })?.id || id

      // Sync lead to convertedToProposal: true and stage WON
      const targetLeadId = leadId || lead?.id
      if (targetLeadId) {
        await leadStore.saveLeadAndConfirm({ id: targetLeadId, stage: LeadStage.WON, convertedToProposal: true })
      }

      message.success(`Proposal ${isEditing ? 'updated' : 'created'} successfully!`)
      // Navigate to the detail page on create so Approve / Reject buttons are visible immediately
      setTimeout(() => navigate(savedId && !isEditing ? `/proposals/${savedId}` : '/proposals'), 1500)
    } catch {
      message.error('Failed to save proposal')
    } finally {
      setSubmitting(false)
    }
  }

  const handleAddPricingRow = () => {
    setPricingRows([...pricingRows, {
      supplySource: '', volumePerAnnum: '', drawlVoltage: '',
      rate: 0, equityInvestment: 'NIL', securityMechanism: '',
    }])
  }

  const handleRemovePricingRow = (index: number) => {
    setPricingRows(pricingRows.filter((_, i) => i !== index))
  }

  const handlePricingChange = (index: number, field: string, value: any) => {
    const newRows = [...pricingRows]
    newRows[index] = { ...newRows[index], [field]: value }
    setPricingRows(newRows)
  }

  const pricingColumns = [
    {
      title: 'Type of Power', dataIndex: 'supplySource', width: 160,
      render: (_: any, record: PricingDetail, index: number) => (
        <Select
          value={record.supplySource}
          onChange={(val) => handlePricingChange(index, 'supplySource', val)}
          options={supplySourceOptions}
          size="small" style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Volume/Annum', dataIndex: 'volumePerAnnum', width: 140,
      render: (_: any, record: PricingDetail, index: number) => (
        <Input value={record.volumePerAnnum}
          onChange={(e) => handlePricingChange(index, 'volumePerAnnum', e.target.value)}
          size="small" placeholder="e.g., 2.13 Crores" />
      ),
    },
    {
      title: 'Drawl Voltage', dataIndex: 'drawlVoltage', width: 140,
      render: (_: any, record: PricingDetail, index: number) => (
        <Select
          value={record.drawlVoltage || undefined}
          onChange={(val) => handlePricingChange(index, 'drawlVoltage', val)}
          options={drawlVoltageOptions}
          size="small" style={{ width: '100%' }}
          placeholder="Select from Master"
          showSearch
          optionFilterProp="label"
        />
      ),
    },
    {
      title: 'Sale Power Price', dataIndex: 'rate', width: 150,
      render: (_: any, record: PricingDetail, index: number) => (
        <InputNumber value={record.rate}
          onChange={(val) => handlePricingChange(index, 'rate', val)}
          size="small" min={0} step={0.01} precision={2} style={{ width: '100%' }} />
      ),
    },
    {
      title: 'Equity Investment', dataIndex: 'equityInvestment', width: 130,
      render: (_: any, record: PricingDetail, index: number) => (
        <Input value={record.equityInvestment}
          onChange={(e) => handlePricingChange(index, 'equityInvestment', e.target.value)}
          size="small" placeholder="NIL or amount" />
      ),
    },
    {
      title: 'Security Mechanism', dataIndex: 'securityMechanism', width: 190,
      render: (_: any, record: PricingDetail, index: number) => (
        <Select
          value={record.securityMechanism || undefined}
          onChange={(val) => handlePricingChange(index, 'securityMechanism', val)}
          options={SECURITY_MECHANISM_OPTIONS}
          size="small" style={{ width: '100%' }}
          placeholder="Select" allowClear
        />
      ),
    },
    {
      title: '', width: 60,
      render: (_: any, _r: PricingDetail, index: number) =>
        pricingRows.length > 1 && (
          <Button danger size="small" icon={<DeleteOutlined />}
            onClick={() => handleRemovePricingRow(index)} />
        ),
    },
  ]

  return (
    <div className="lead-form-container">
      {/* Header */}
      <div className="form-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button type="text" icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/proposals')} style={{ fontSize: '18px' }} />
          <div>
            <h2 style={{ margin: 0 }}>
              {isNegotiating ? '🤝 Negotiate Proposal' : isEditing ? '✏️ Edit Proposal' : '📋 Create New Proposal'}
            </h2>
            {selectedTemplate && (
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                Template: <strong style={{ color: '#001A4D' }}>{selectedTemplate.name}</strong>
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button icon={<EyeOutlined />} onClick={async () => {
            if (previewMode) {
              setPreviewMode(false)
              return
            }
            try {
              const values = await form.validateFields()
              setFormValues({ ...values, pricingDetails: pricingRows })
              setPreviewMode(true)
            } catch {
              message.error('Please fill in all required fields before previewing')
            }
          }}>
            {previewMode ? 'Back to Edit' : 'Preview'}
          </Button>
          {isEditing && <Button icon={<CopyOutlined />}>Create Revision</Button>}
        </div>
      </div>

      {isNegotiating && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16, borderRadius: 8 }}
          message="Negotiating an already-approved proposal"
          description="This proposal is Approved. Any commercial/contract term you change here will be recorded in Revision History (visible on the proposal's detail page), and the proposal stays Approved with the updated terms after you save."
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        style={{ display: previewMode ? 'none' : 'block' }}
        initialValues={{
          offerDate: dayjs(),                 // default to today
          referenceNumber: autoRefNumber,     // auto-generated
          ...(fromLead ? { leadId: fromLead.id, gstDetails: { placeOfSupply: fromLead.city } } : {}),
        }}
      >

        {/* ── Step 1: Lead & Template ─────────────────────────────── */}
        <Card
          title="Step 1: Select Lead & Template"
          style={{ marginBottom: '16px' }}
          styles={{ header: { background: '#f5f5f5', borderBottom: '2px solid #001A4D' } }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item name="leadId" label="Select Lead"
                rules={[{ required: true, message: 'Lead selection is required' }]}>
                <Select
                  placeholder="Select a lead to create proposal"
                  size="large" showSearch optionFilterProp="label"
                  options={leadOptions} onChange={handleLeadChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="template" label={
                <span>
                  Proposal Template
                  <Tag
                    color="blue"
                    style={{ marginLeft: 8, fontSize: 10, cursor: 'pointer' }}
                    onClick={() => navigate('/master/proposal-templates')}
                  >
                    <FileTextOutlined /> Manage Templates
                  </Tag>
                </span>
              }
                rules={[{ required: true, message: 'Template selection required' }]}>
                <Select
                  placeholder={activeTemplates.length === 0 ? 'No templates — go to Master to create one' : 'Choose a proposal template'}
                  size="large"
                  optionLabelProp="label"
                  disabled={activeTemplates.length === 0}
                  onChange={handleTemplateChange}
                >
                  {activeTemplates.map(t => (
                    <Select.Option key={t.id} value={t.id} label={t.name}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 8,
                          background: '#eff6ff', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          color: '#001A4D', fontSize: 16, flexShrink: 0,
                        }}>
                          <FileTextOutlined />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>
                            {t.name}
                            {t.isDefault && <Tag color="gold" style={{ marginLeft: 6, fontSize: 10 }}>Default</Tag>}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{t.description}</div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
                            Contract: {t.contractPeriod}yr · Lock-in: {t.lockInPeriod}yr · Validity: {t.validity}d · PF: ₹{t.powerFacilitationCharge}/unit
                          </div>
                        </div>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {selectedLeadObj && isLeadDeactive(selectedLeadObj) && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message={`Lead "${selectedLeadObj.companyName}" is missing its HTSC Number or GST Number`}
              description={
                <div>
                  You can still create this proposal — but it's worth updating the lead's HTSC & GST details when you get a chance.
                  <div style={{ marginTop: 8 }}>
                    <Button
                      size="small"
                      onClick={() => navigate(`/leads/${selectedLeadObj.id}/edit`)}
                    >
                      Edit Lead to Add HTSC & GST Numbers
                    </Button>
                  </div>
                </div>
              }
            />
          )}

          {existingActiveProposalForLead && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 8 }}
              message="This lead already has an active proposal"
              description={
                <span>
                  {existingActiveProposalForLead.leadName || existingActiveProposalForLead.leadId} already has proposal{' '}
                  <strong>{existingActiveProposalForLead.referenceNumber || existingActiveProposalForLead.id}</strong>{' '}
                  ({existingActiveProposalForLead.status}). A new proposal can only be created for this lead once that
                  one is Rejected or Expired — until then,{' '}
                  <span
                    style={{ color: '#001A4D', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => navigate(`/proposals/${existingActiveProposalForLead.id}`)}
                  >
                    open the existing proposal
                  </span> to revise it instead.
                </span>
              }
            />
          )}

          {/* Template preview banner — shows LIVE form values (reflects edits in real time) */}
          {selectedTemplate && (
            <Alert
              message={
                <span style={{ fontWeight: 600 }}>
                  📋 {selectedTemplate.name} — T&C Summary
                </span>
              }
              description={
                <div style={{ fontSize: 12, marginTop: 6 }}>
                  <Row gutter={[16, 4]}>
                    <Col xs={24} sm={12}>
                      <p style={{ margin: '2px 0' }}>📅 Contract: <strong>{watchedContractTerms?.contractPeriod ?? selectedTemplate.contractPeriod}yr</strong> / Lock-in: <strong>{watchedContractTerms?.lockInPeriod ?? selectedTemplate.lockInPeriod}yr</strong></p>
                      <p style={{ margin: '2px 0' }}>🔔 Notice: <strong>{watchedContractTerms?.noticePeriod ?? selectedTemplate.noticePeriod} months</strong></p>
                      <p style={{ margin: '2px 0' }}>⏰ Validity: <strong>{watchedValidity ?? selectedTemplate.validity} days</strong></p>
                    </Col>
                    <Col xs={24} sm={12}>
                      <p style={{ margin: '2px 0' }}>⚡ PF Charge: <strong>₹{watchedPfCharge ?? selectedTemplate.powerFacilitationCharge}/unit + {watchedGst ?? selectedTemplate.gstOnFacilitationCharge}% GST</strong></p>
                      <p style={{ margin: '2px 0' }}>📊 Min Supply: <strong>{watchedBillingTerms?.minimumAssuredSupply ?? selectedTemplate.minimumAssuredSupply}</strong></p>
                      <p style={{ margin: '2px 0' }}>💳 Payment Due: <strong>{watchedBillingTerms?.paymentDueInDays ?? selectedTemplate.paymentDueInDays} days</strong></p>
                    </Col>
                  </Row>
                  <p style={{ margin: '6px 0 0' }}>
                    📄 Document pages:{' '}
                    <Tag color={(watchedCoverPageEnabled ?? selectedTemplate.coverPageEnabled) ? 'blue' : 'default'}>
                      {(watchedCoverPageEnabled ?? selectedTemplate.coverPageEnabled) ? '✓ Cover page' : 'No cover page'}
                    </Tag>
                    <Tag color={(watchedCompanyContentHtml ?? selectedTemplate.companyContentHtml ?? '').trim() ? 'green' : 'default'}>
                      {(watchedCompanyContentHtml ?? selectedTemplate.companyContentHtml ?? '').trim() ? '✓ Company content' : 'No company page'}
                    </Tag>
                    <Tag color="purple">Proposal letter</Tag>
                  </p>
                  <p style={{ margin: '6px 0 0', color: '#64748b', fontStyle: 'italic' }}>
                    All T&amp;C clauses (A–H) loaded from master. Edit in Step 7 below or go to{' '}
                    <span
                      style={{ color: '#001A4D', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => navigate('/master/proposal-templates')}
                    >
                      Master → Proposal Templates
                    </span>
                  </p>
                </div>
              }
              type="success"
              showIcon
              style={{ marginTop: 8 }}
            />
          )}

          {activeTemplates.length === 0 && (
            <Alert
              message="No active templates found"
              description={
                <span>
                  Go to{' '}
                  <span
                    style={{ color: '#001A4D', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => navigate('/master/proposal-templates')}
                  >
                    Master → Proposal Templates
                  </span>{' '}
                  to create and activate templates first.
                </span>
              }
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
            />
          )}

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12}>
              <Form.Item name="referenceNumber" label="Reference Number (auto-generated)">
                <Input
                  size="large"
                  readOnly
                  style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, background: '#f8fafc' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="offerDate" label="Offer Date">
                <DatePicker size="large" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {leadInfo && (
            <Alert
              message={leadInfo.name}
              description={
                <div style={{ fontSize: '12px', marginTop: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px' }}>
                  <p style={{ margin: 0 }}><strong>Contact:</strong> {leadInfo.contactPerson}</p>
                  <p style={{ margin: 0 }}><strong>Phone:</strong> {leadInfo.phone}</p>
                  <p style={{ margin: 0 }}><strong>Email:</strong> {leadInfo.email}</p>
                  <p style={{ margin: 0 }}><strong>City:</strong> {leadInfo.city}</p>
                  <p style={{ margin: 0 }}><strong>Sanctioned Demand:</strong> {leadInfo.sanctionedDemand} KW</p>
                  <p style={{ margin: 0 }}><strong>Drawl Voltage:</strong> {leadInfo.drawlVoltage}</p>
                </div>
              }
              type="info" showIcon style={{ marginTop: '8px' }}
            />
          )}
        </Card>

        {/* ── Step 2: Pricing Details ──────────────────────────────── */}
        <Card
          title="Step 2: Pricing Details"
          style={{ marginBottom: '16px' }}
          styles={{ header: { background: '#f5f5f5', borderBottom: '2px solid #001A4D' } }}
        >
          <Table
            dataSource={pricingRows}
            columns={pricingColumns}
            rowKey={(_, index) => `pricing-${index}`}
            pagination={false} size="small" scroll={{ x: true }}
            style={{ marginBottom: '16px' }}
          />
          <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddPricingRow} block>
            Add Pricing Row
          </Button>

          <Divider />

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="sakthiTariff"
                label="Sakthi PF Charge (₹/unit)"
                rules={[{ required: true, message: 'Sakthi PF Charge is required' }]}
              >
                <Select
                  placeholder="Select from Sakthi Power Tariff Master"
                  size="large"
                  options={sakthiTariffOptions}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="gstOnFacilitationCharge" label="GST on Facilitation Charge (%)">
                <InputNumber placeholder="e.g., 18" size="large" min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Step 3: Contract Terms ───────────────────────────────── */}
        <Card
          title="Step 3: Contract Terms"
          style={{ marginBottom: '16px' }}
          styles={{ header: { background: '#f5f5f5', borderBottom: '2px solid #001A4D' } }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item name={['contractTerms', 'contractPeriod']} label="Contract Period (Years)" rules={[{ required: true }]}>
                <InputNumber placeholder="e.g., 3" size="large" min={1} max={10} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name={['contractTerms', 'lockInPeriod']} label="Lock-in Period (Years)" rules={[{ required: true }]}>
                <InputNumber placeholder="e.g., 2" size="large" min={0} max={10} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item name={['contractTerms', 'noticePeriod']} label="Notice Period (Months)" rules={[{ required: true }]}>
                <InputNumber placeholder="e.g., 6" size="large" min={0} max={24} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name={['contractTerms', 'commencementMonth']} label="Commencement Month">
                <Input placeholder="e.g., July 2026" size="large" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Step 4: Billing & Payment ────────────────────────────── */}
        <Card
          title="Step 4: Billing & Payment Terms"
          style={{ marginBottom: '16px' }}
          styles={{ header: { background: '#f5f5f5', borderBottom: '2px solid #001A4D' } }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item name={['billingTerms', 'billRaiseMethod']} label="Bill Raising Method" rules={[{ required: true }]}>
                <Input placeholder="e.g., Based on TANGEDCO Energy Allocation statement" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name={['billingTerms', 'paymentDueInDays']} label="Payment Due (Days)" rules={[{ required: true }]}>
                <InputNumber placeholder="e.g., 5" size="large" min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item name={['billingTerms', 'minimumAssuredSupply']} label="Minimum Assured Supply">
                <Input placeholder="e.g., 80 to 85% of contracted quantum" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name={['billingTerms', 'rateRevision']} label="Rate Revision">
                <Input placeholder="e.g., 50-50 basis" size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="energySupplyAdjustments" label="Energy Supply & Adjustments">
            <Input.TextArea placeholder="Peak hour, Normal hour, Off-peak adjustments..." rows={3} size="large" />
          </Form.Item>
        </Card>

        {/* ── Required Documents (from the selected Proposal Template) ─── */}
        <Card
          title={
            <span>
              Required Documents
              <Tag
                color="geekblue"
                style={{ marginLeft: 8, fontSize: 10, cursor: 'pointer' }}
                onClick={() => navigate('/master/proposal-templates')}
              >
                <FileProtectOutlined /> Manage in Template
              </Tag>
            </span>
          }
          style={{ marginBottom: '16px' }}
          styles={{ header: { background: '#f5f5f5', borderBottom: '2px solid #001A4D' } }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="requiredDocuments"
                label="Documents required from customer"
                tooltip="Pre-filled from the selected template. Type to add or remove documents for this proposal."
              >
                <Select
                  mode="tags"
                  size="large"
                  placeholder={selectedTemplate ? 'Add required documents…' : 'Select a template first (Step 1)'}
                  tokenSeparators={[',']}
                  options={(selectedTemplate?.requiredDocuments ?? []).map(d => ({ label: d, value: d }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="signingDocuments"
                label="Documents to be signed"
                tooltip="Pre-filled from the selected template. Type to add or remove."
              >
                <Select
                  mode="tags"
                  size="large"
                  placeholder={selectedTemplate ? 'Add documents to sign…' : 'Select a template first (Step 1)'}
                  tokenSeparators={[',']}
                  options={(selectedTemplate?.signingDocuments ?? []).map(d => ({ label: d, value: d }))}
                />
              </Form.Item>
            </Col>
          </Row>

          {(watchedRequiredDocs.length > 0 || watchedSigningDocs.length > 0) && (
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 16px' }}>
              {watchedRequiredDocs.length > 0 && (
                <div style={{ marginBottom: watchedSigningDocs.length > 0 ? 12 : 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: '#1e40af', marginBottom: 4 }}>
                    📎 Required from customer
                  </div>
                  <ol style={{ margin: 0, paddingLeft: 20, fontSize: 12.5, color: '#334155' }}>
                    {watchedRequiredDocs.map((d, i) => <li key={i} style={{ marginBottom: 2 }}>{d}</li>)}
                  </ol>
                </div>
              )}
              {watchedSigningDocs.length > 0 && (
                <div>
                  <div style={{ fontWeight: 700, fontSize: 12, color: '#1e40af', marginBottom: 4 }}>
                    ✍️ Documents to sign
                  </div>
                  <ol style={{ margin: 0, paddingLeft: 20, fontSize: 12.5, color: '#334155' }}>
                    {watchedSigningDocs.map((d, i) => <li key={i} style={{ marginBottom: 2 }}>{d}</li>)}
                  </ol>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* ── Step 5: GST & Tax ───────────────────────────────────── */}
        <Card
          title="Step 5: GST & Tax Configuration"
          style={{ marginBottom: '16px' }}
          styles={{ header: { background: '#f5f5f5', borderBottom: '2px solid #001A4D' } }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item name={['gstDetails', 'hsn']} label="HSN Code"
                rules={[{ required: true, message: 'HSN code required' }]}>
                <Select placeholder="Select HSN code" size="large"
                  options={[
                    { label: '271311 - Electrical Energy', value: '271311' },
                    { label: '2716 - Electricity', value: '2716' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name={['gstDetails', 'placeOfSupply']} label="Place of Supply" rules={[{ required: true }]}>
                <Input placeholder={leadInfo?.city || 'Enter place of supply'} size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item name={['gstDetails', 'reverseCharge']} valuePropName="checked">
                <Checkbox>
                  Apply Reverse Charge
                  <Tooltip title="Receiver liable for tax">ℹ️</Tooltip>
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Step 6: Validity & Additional Info ──────────────────── */}
        <Card
          title="Step 6: Validity & Additional Information"
          style={{ marginBottom: '16px' }}
          styles={{ header: { background: '#f5f5f5', borderBottom: '2px solid #001A4D' } }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item name="validity" label="Proposal Validity" rules={[{ required: true }]}>
                <Select placeholder="How long is proposal valid" size="large"
                  options={[
                    { label: '15 Days', value: 15 },
                    { label: '30 Days', value: 30 },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="monthlyConsumption" label="Estimated Monthly Consumption (kWh)">
                <InputNumber placeholder="Optional" size="large" min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name={['gstDetails', 'sac']} style={{ display: 'none' }}><Input /></Form.Item>
          <Form.Item name="additionalTerms" label="Additional Terms & Conditions">
            <Input.TextArea placeholder="Any special terms, conditions, or notes..." rows={3} size="large" />
          </Form.Item>
        </Card>

        {/* ── Step 7: Customize Document Layout & Text ────────────── */}
        <Card
          title="Step 7: Customize Document Layout & Text"
          style={{ marginBottom: '16px' }}
          styles={{ header: { background: '#f5f5f5', borderBottom: '2px solid #001A4D' } }}
        >
          <Tabs
            items={[
              {
                key: 'body',
                label: '📝 Proposal Body',
                children: (
                  <div style={{ paddingTop: 12 }}>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Customize the proposal letter body for this customer. These edits only apply to this proposal."
                      description={
                        <span style={{ fontSize: 12 }}>
                          Use placeholders and they get filled at download time: {' '}
                          <code style={{ color: '#0F9D6C', background: '#E6F7EF', padding: '1px 6px', borderRadius: 4 }}>{'{{PRICING_TABLE}}'}</code>{' '}
                          <code style={{ color: '#0F9D6C', background: '#E6F7EF', padding: '1px 6px', borderRadius: 4 }}>{'{{validityDate}}'}</code>{' '}
                          <code style={{ color: '#0F9D6C', background: '#E6F7EF', padding: '1px 6px', borderRadius: 4 }}>{'{{customerName}}'}</code>{' '}
                          <code style={{ color: '#0F9D6C', background: '#E6F7EF', padding: '1px 6px', borderRadius: 4 }}>{'{{customerCity}}'}</code>
                          <br /><br />
                          <strong>Commercial Terms (this proposal's own values):</strong>{' '}
                          {COMMERCIAL_TERMS_PLACEHOLDERS.map((label) => (
                            <code key={label} style={{ color: '#0F9D6C', background: '#E6F7EF', padding: '1px 6px', borderRadius: 4, marginRight: 4, display: 'inline-block', marginBottom: 4 }}>{`{${label}}`}</code>
                          ))}
                        </span>
                      }
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                      <Button size="small" onClick={() => {
                        if (selectedTemplate) {
                          form.setFieldValue('bodyHtml', selectedTemplate.bodyHtml || buildTemplateBody(selectedTemplate));
                          message.success('Reset body text to template default.');
                        } else {
                          message.warning('Please select a template in Step 1 first.');
                        }
                      }}>
                        Reset to Template Default
                      </Button>
                    </div>
                    <Form.Item name="bodyHtml" valuePropName="value" trigger="onChange">
                      <RichTextEditor
                        placeholder="Type the full proposal body here..."
                        minHeight={360}
                      />
                    </Form.Item>
                  </div>
                ),
              },
              {
                key: 'cover',
                label: '🖼️ Cover Page',
                children: (
                  <div style={{ paddingTop: 12 }}>
                    <Form.Item name="coverPageEnabled" label="Include Cover Page" valuePropName="checked">
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                      <Form.Item name="coverTitle" label="Cover Title">
                        <Input placeholder="e.g. Commercial Proposal" />
                      </Form.Item>
                      <Form.Item name="coverSubtitle" label="Cover Subtitle / Tagline">
                        <Input placeholder="e.g. Third Party & Group Captive Power" />
                      </Form.Item>
                    </div>
                    <Form.Item name="coverDescription" label="Cover Description">
                      <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} placeholder="Cover page description..." />
                    </Form.Item>
                    <Form.Item name="coverImage" label="Cover Background Image (optional, watermark)">
                      <CoverImageUpload />
                    </Form.Item>
                  </div>
                ),
              },
              {
                key: 'company',
                label: '🏢 Company Content',
                children: (
                  <div style={{ paddingTop: 12 }}>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Company Profile / About Us page. Leave empty to skip."
                    />
                    <Form.Item name="companyContentHtml" valuePropName="value" trigger="onChange">
                      <RichTextEditor
                        placeholder="Company profile details..."
                        minHeight={360}
                      />
                    </Form.Item>
                  </div>
                ),
              },
              {
                key: 'terms',
                label: '📜 Terms & Conditions',
                children: (
                  <div style={{ paddingTop: 12 }}>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 14 }}
                      message="Custom Terms & Conditions for this specific proposal. Edits here will NOT change the master template."
                      description={
                        <span style={{ fontSize: 12 }}>
                          Use these placeholders and they get filled at download time:{' '}
                          {COMMERCIAL_TERMS_PLACEHOLDERS.map((label) => (
                            <code key={label} style={{ color: '#0F9D6C', background: '#E6F7EF', padding: '1px 6px', borderRadius: 4, marginRight: 4, display: 'inline-block', marginBottom: 4 }}>{`{${label}}`}</code>
                          ))}
                        </span>
                      }
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                      <Button size="small" onClick={() => {
                        if (selectedTemplate) {
                          form.setFieldValue('termsConditionsHtml', getTemplateTermsHtml(selectedTemplate));
                          message.success('Terms & Conditions reset to template default.');
                        } else {
                          message.warning('Please select a template in Step 1 first.');
                        }
                      }}>
                        Reset to Template Default
                      </Button>
                    </div>
                    <Form.Item name="termsConditionsHtml" valuePropName="value" trigger="onChange">
                      <RichTextEditor
                        placeholder="Type the Terms & Conditions here..."
                        minHeight={320}
                      />
                    </Form.Item>
                  </div>
                ),
              },
            ]}
          />
        </Card>

        <Divider />

        {/* Form Actions */}
        <div className="form-actions" style={{ marginBottom: '24px' }}>
          <Button size="large" onClick={() => navigate('/proposals')} disabled={submitting}>Cancel</Button>
          {isEditing && (
            <Button size="large" type="dashed" onClick={() => message.info('Proposal saved as draft')}>
              Save as Draft
            </Button>
          )}
          <Button
            type="primary" htmlType="submit" size="large" icon={<SaveOutlined />}
            loading={submitting}
            disabled={(!selectedTemplate && activeTemplates.length > 0) || !!existingActiveProposalForLead}
          >
            {isEditing ? 'Update & Submit Proposal' : 'Create & Submit Proposal'}
          </Button>
        </div>
      </Form>

      {/* ── Preview Mode ─────────────────────────────────────────── */}
      {previewMode && formValues && (
        <Card title="📄 Proposal Preview" style={{ marginTop: '24px' }}>
          <Tabs items={[
            {
              key: 'summary', label: 'Summary',
              children: (
                <div style={{ padding: '20px 0' }}>
                  <h4>Proposal Reference</h4>
                  <Row gutter={[32, 16]}>
                    <Col xs={24} sm={12}>
                      <p><strong>Reference No:</strong> {formValues.referenceNumber || 'N/A'}</p>
                      <p><strong>Offer Date:</strong> {formValues.offerDate ? dayjs(formValues.offerDate).format('DD-MM-YYYY') : 'N/A'}</p>
                    </Col>
                    <Col xs={24} sm={12}>
                      <p><strong>Validity:</strong> {formValues.validity} days</p>
                      <p><strong>Template:</strong> {selectedTemplate?.name || formValues.template}</p>
                    </Col>
                  </Row>
                  <Divider />
                  <h4>Contract Terms</h4>
                  <Row gutter={[32, 16]}>
                    <Col xs={24} sm={12}>
                      <p><strong>Contract Period:</strong> {formValues.contractTerms?.contractPeriod || 'N/A'} years</p>
                      <p><strong>Lock-in Period:</strong> {formValues.contractTerms?.lockInPeriod || 'N/A'} years</p>
                    </Col>
                    <Col xs={24} sm={12}>
                      <p><strong>Notice Period:</strong> {formValues.contractTerms?.noticePeriod || 'N/A'} months</p>
                      <p><strong>Commencement:</strong> {formValues.contractTerms?.commencementMonth || 'N/A'}</p>
                    </Col>
                  </Row>
                </div>
              ),
            },
            {
              key: 'pricing', label: 'Pricing Details',
              children: (
                <div style={{ padding: '20px 0' }}>
                  <Table
                    dataSource={pricingRows}
                    columns={[
                      { title: 'Supply Source', dataIndex: 'supplySource', key: 'supplySource' },
                      { title: 'Volume', dataIndex: 'volumePerAnnum', key: 'volumePerAnnum' },
                      { title: 'Voltage', dataIndex: 'drawlVoltage', key: 'drawlVoltage' },
                      { title: 'Sale Power Price', dataIndex: 'rate', key: 'rate', render: (val) => `₹${val}` },
                      { title: 'Equity', dataIndex: 'equityInvestment', key: 'equityInvestment' },
                      { title: 'Security', dataIndex: 'securityMechanism', key: 'securityMechanism' },
                    ]}
                    rowKey={(_, index) => `preview-pricing-${index}`}
                    pagination={false} size="small"
                  />
                  {formValues.sakthiTariff && (
                    <>
                      <Divider />
                      <p><strong>PF Charge:</strong> ₹{formValues.sakthiTariff}/unit + {formValues.gstOnFacilitationCharge || 0}% GST</p>
                    </>
                  )}
                </div>
              ),
            },
          ]} />
        </Card>
      )}

      {/* ── Quick Add Sakthi Power Tariff Modal ── */}
      <Modal
        title="Add Sakthi Power Tariff to Master"
        open={addTariffModalOpen}
        onCancel={() => setAddTariffModalOpen(false)}
        footer={null}
        destroyOnClose
        width={500}
      >
        <Form
          form={addTariffForm}
          layout="vertical"
          onFinish={async (values: any) => {
            setAddTariffSubmitting(true)
            try {
              let tariffCode = ''
              try {
                tariffCode = await generateCode('tariffs')
              } catch {
                tariffCode = `T${Date.now().toString().slice(-4)}`
              }
              const pfChargeInRupees = values.powerFacilitationCharge ? Number(values.powerFacilitationCharge) / 100 : 0
              const payload = {
                id: genId('T'),
                tariffCode,
                description: values.description || `Sakthi Power Tariff (${tariffCode})`,
                ratePerUnit: pfChargeInRupees,
                powerFacilitationCharge: pfChargeInRupees,
                applicableTo: values.customerType === 'generator' ? 'generator' : 'consumer',
                consumerId: values.customerType === 'consumer' ? values.consumerId : undefined,
                generatorId: values.customerType === 'generator' ? values.generatorId : undefined,
                effectiveFrom: values.effectiveFrom || new Date().toISOString().slice(0, 10),
                effectiveTo: '',
                status: 'active',
              }
              const saved = await saveTariff(payload, false)
              const pfRate = saved.powerFacilitationCharge ?? saved.ratePerUnit ?? 0
              form.setFieldValue('sakthiTariff', pfRate)
              message.success(`Sakthi Power Tariff "${saved.tariffCode}" added to Master and applied!`)
              setAddTariffModalOpen(false)
            } catch (err) {
              message.error(err instanceof ApiError && err.message ? err.message : 'Failed to save tariff to Master')
            } finally {
              setAddTariffSubmitting(false)
            }
          }}
        >
          <Form.Item name="customerType" label="Customer Type" initialValue="consumer" rules={[{ required: true }]}>
            <Radio.Group onChange={() => { addTariffForm.setFieldValue('generatorId', undefined); addTariffForm.setFieldValue('consumerId', undefined); }}>
              <Radio value="consumer">Power Consumer</Radio>
              <Radio value="generator">Power Generator</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.customerType !== curr.customerType}
          >
            {({ getFieldValue }) => {
              const customerType = getFieldValue('customerType') || 'consumer'
              return customerType === 'generator' ? (
                <Form.Item name="generatorId" label="Generator Name">
                  <Select
                    allowClear showSearch optionFilterProp="label"
                    placeholder="Select generator"
                    options={generators.filter(g => g.status === 'active').map(g => ({ value: g.id, label: `${g.name} (${g.code})` }))}
                  />
                </Form.Item>
              ) : (
                <Form.Item name="consumerId" label="Consumer Name">
                  <Select
                    allowClear showSearch optionFilterProp="label"
                    placeholder="Select consumer"
                    options={consumers.filter(c => c.status === 'active').map(c => ({ value: c.id, label: `${c.name} (${c.code})` }))}
                  />
                </Form.Item>
              )
            }}
          </Form.Item>

          <Form.Item name="powerFacilitationCharge" label="PF Charge (Paise/unit)" rules={[{ required: true, message: 'PF Charge is required' }]}>
            <InputNumber min={0} step={1} precision={0} style={{ width: '100%' }} addonAfter="Paise" placeholder="e.g. 50" />
          </Form.Item>

          <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Description is required' }]}>
            <Input placeholder="e.g. Sakthi Power Tariff for ABC Company" />
          </Form.Item>

          <Form.Item name="effectiveFrom" label="Effective From" rules={[{ required: true }]}>
            <Input type="date" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <Button onClick={() => setAddTariffModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={addTariffSubmitting}>
              Save to Master & Apply
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  )
}

export default ProposalForm
